import { useMemo, useState, useEffect } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useGetDeliveryLogsQuery } from '@/store/api/communicationApi';
import { wsClient } from '@/lib/ws';
import { COMMUNICATION_UPDATES_ROOM, CommunicationEventType } from '@cluster/types';
import type { DeliveryLog } from '@/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { EmptyState } from '@/components/shared/EmptyState';
import { cn } from '@/lib/utils';

interface DeliveryLogsTableProps {
  webhookId: string;
  onSelectLog?: (log: DeliveryLog) => void;
}

const outcomeStyle: Record<DeliveryLog['outcome'], string> = {
  success: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  denied: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  timeout: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  'quota-exceeded': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
};

export function DeliveryLogsTable({ webhookId, onSelectLog }: DeliveryLogsTableProps) {
  const [page, setPage] = useState(1);
  const limit = 10;
  const { data, isLoading, isFetching, isError, refetch } = useGetDeliveryLogsQuery({
    webhookId,
    page,
    limit,
  });
  const logs = data?.data ?? [];
  const totalPages = data?.pagination.totalPages ?? 1;

  // Real-time updates via the platform gateway.
  // Communication publishes delivery-log events to Redis (COMMUNICATION_EVENTS_CHANNEL);
  // the gateway fans them out to the Socket.io room COMMUNICATION_UPDATES_ROOM. The
  // authenticated wsClient (JWT) receives them and refetches the log list.
  useEffect(() => {
    const onLogEvent = () => {
      refetch();
    };

    const unsubConnected = wsClient.on('connected', () => {
      wsClient.subscribe(COMMUNICATION_UPDATES_ROOM).catch(() => {});
    });

    if (!wsClient.connected) {
      wsClient.connect().catch(() => {});
    } else {
      wsClient.subscribe(COMMUNICATION_UPDATES_ROOM).catch(() => {});
    }

    const unsubs = [
      unsubConnected,
      wsClient.on(CommunicationEventType.DELIVERY_RECORDED, onLogEvent),
      wsClient.on(CommunicationEventType.DELIVERY_EMIT, onLogEvent),
    ];

    return () => {
      unsubs.forEach((unsub) => unsub());
      wsClient.unsubscribe(COMMUNICATION_UPDATES_ROOM).catch(() => {});
    };
  }, [webhookId, refetch]);

  const columns = useMemo<ColumnDef<DeliveryLog, unknown>[]>(
    () => [
      {
        accessorKey: 'correlationId',
        header: 'Correlation ID',
        cell: ({ row }) => (
          <span className="font-mono text-xs truncate max-w-[180px] block" title={row.original.correlationId}>
            {row.original.correlationId}
          </span>
        ),
      },
      {
        accessorKey: 'outcome',
        header: 'Outcome',
        cell: ({ row }) => (
          <span
            className={cn(
              'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
              outcomeStyle[row.original.outcome],
            )}
          >
            {row.original.outcome}
          </span>
        ),
      },
      {
        accessorKey: 'triggerKind',
        header: 'Kind',
        cell: ({ row }) => <Badge variant="outline">{row.original.triggerKind}</Badge>,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <StatusBadge status={row.original.sandbox.success ? 'executed' : 'error'} />,
      },
      {
        accessorKey: 'totalDurationMs',
        header: 'Duration',
        cell: ({ row }) => <span className="tabular-nums">{row.original.totalDurationMs}ms</span>,
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => format(new Date(row.original.createdAt), 'MMM d, yyyy HH:mm:ss'),
      },
    ],
    [],
  );

  const table = useReactTable({
    data: logs,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: totalPages,
  });

  return (
    <div className="space-y-3">
      {isLoading ? (
        <LoadingSpinner className="py-12" />
      ) : isError ? (
        <p className="py-8 text-center text-sm text-destructive">Failed to load delivery logs.</p>
      ) : logs.length === 0 ? (
        <EmptyState
          title="No delivery logs yet"
          description="Trigger a test run or wait for real deliveries to appear here."
        />
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id} className="border-b bg-muted/50">
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      className="h-10 px-4 text-left font-medium text-muted-foreground"
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.original.id}
                  onClick={() => onSelectLog?.(row.original)}
                  className={cn(
                    'border-b transition-colors hover:bg-muted/30',
                    onSelectLog && 'cursor-pointer',
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Server-side pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Page {page} of {totalPages} ({data?.pagination.total ?? 0} total)
        </p>
        <div className="flex items-center gap-2">
          {isFetching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          <Button
            variant="outline"
            size="icon"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1 || isFetching}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages || isFetching}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}